/**
 * Custom Recharge Widget
 * A production-ready subscription widget that integrates with Recharge selling plans
 * 
 * @version 1.0.0
 * @author Mood Co
 */

class RechargeCustomWidget {
  constructor() {
    this.widget = null;
    this.productId = null;
    this.selectedPlan = null;
    this.productForm = null;
    
    // Configuration mapping selling plan IDs to our custom options
    this.config = {
      sellingPlans: {
        loyalty: {
          id: '710907855228',
          name: 'Loyalty Plan',
          discount: 25,
          price: 35.25,
          subtitle: 'Our most popular plan',
          commitment: '3-month commitment',
          features: [
            'Delivered monthly',
            'Delay, skip or amend frequency to suit your needs',
            '3-month commitment'
          ]
        },
        rolling: {
          id: '710907822460', 
          name: 'Rolling Plan',
          discount: 10,
          price: 42.30,
          subtitle: 'Our most flexible plan',
          commitment: 'Cancel any time',
          features: [
            'Delivered monthly',
            'Delay, skip or amend frequency to suit your needs',
            'Cancel any time'
          ]
        },
        onetime: {
          id: null,
          name: 'One time purchase',
          discount: 0,
          price: 47.00,
          subtitle: 'Single bottle',
          commitment: null,
          features: [
            'One month\'s supply'
          ]
        }
      }
    };
  }

  /**
   * Initialize the widget
   */
  async init() {
    try {
      // Wait for Recharge SDK to be available
      await this.waitForRechargeSDK();
      
      // Find our custom widget element
      this.widget = document.querySelector('.custom-recharge-widget');
      if (!this.widget) return;

      // Get product information
      this.productId = this.getProductId();
      this.productForm = this.getProductForm();
      
      if (!this.productId || !this.productForm) return;

      // Initialize widget functionality
      this.bindEvents();
      this.setInitialSelection();
      
    } catch (error) {
      // Silent fail in production - could add error tracking here
      return;
    }
  }

  /**
   * Wait for Recharge SDK to be available
   */
  waitForRechargeSDK() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds
      
      const checkSDK = () => {
        attempts++;
        
        if (window.Recharge || window.RechargeSDK || window.rc) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Recharge SDK not found'));
        } else {
          setTimeout(checkSDK, 100);
        }
      };
      
      checkSDK();
    });
  }

  /**
   * Get product ID from the page
   */
  getProductId() {
    // Try multiple methods to get product ID
    const productIdElement = document.querySelector('#custom-recharge-product-id, #subscription-product-id');
    if (productIdElement) {
      return productIdElement.value;
    }

    // Try from form
    const form = document.querySelector('.product-form form, form[action*="/cart/add"]');
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        return idInput.value;
      }
    }

    // Try from URL or meta tags
    const productMeta = document.querySelector('meta[property="product:id"]');
    if (productMeta) {
      return productMeta.content;
    }

    return null;
  }

  /**
   * Get the product form
   */
  getProductForm() {
    return document.querySelector('.product-form form, form[action*="/cart/add"]');
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const radioButtons = this.widget.querySelectorAll('.recharge-radio');
    
    radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handlePlanSelection(e.target);
        }
      });
    });
  }

  /**
   * Set initial selection (default to loyalty plan)
   */
  setInitialSelection() {
    const defaultRadio = this.widget.querySelector('input[checked], .recharge-radio[data-price]');
    if (defaultRadio) {
      this.handlePlanSelection(defaultRadio);
    }
  }

  /**
   * Handle plan selection
   */
  handlePlanSelection(radioElement) {
    const plan = radioElement.value;
    const price = parseFloat(radioElement.dataset.price);
    const discount = parseInt(radioElement.dataset.discount) || 0;
    const rechargeOption = radioElement.closest('.recharge-option');
    const sellingPlanId = rechargeOption ? rechargeOption.dataset.sellingPlanId : null;

    this.selectedPlan = {
      type: plan,
      price: price,
      discount: discount,
      sellingPlanId: sellingPlanId || null
    };

    // Update UI
    this.updateSelectedOptionUI(radioElement);
    this.updatePriceDisplay(price);
    
    // Update Shopify form
    this.updateProductForm();
    
    // Fire custom event for other integrations
    this.fireSelectionEvent();
  }

  /**
   * Update selected option UI
   */
  updateSelectedOptionUI(selectedRadio) {
    // Remove selected class from all options
    this.widget.querySelectorAll('.recharge-option').forEach(option => {
      option.classList.remove('selected');
    });
    
    // Add selected class to current option
    const selectedOption = selectedRadio.closest('.recharge-option');
    if (selectedOption) {
      selectedOption.classList.add('selected');
    }
  }

  /**
   * Update price display throughout the page
   */
  updatePriceDisplay(price) {
    const priceElements = document.querySelectorAll(
      '.product-price__current-price, .price__current, .price'
    );
    
    priceElements.forEach(element => {
      if (element && !element.closest('.custom-recharge-widget')) {
        element.textContent = `£${price.toFixed(2)}`;
      }
    });

    // Show/hide compare at price for subscriptions
    const compareElements = document.querySelectorAll(
      '.product-price__compare-price, .price__compare'
    );
    
    compareElements.forEach(element => {
      if (this.selectedPlan.type !== 'onetime') {
        const originalPrice = 47.00;
        element.textContent = `£${originalPrice.toFixed(2)}`;
        element.style.display = 'inline';
      } else {
        element.style.display = 'none';
      }
    });
  }

  /**
   * Update the product form with selling plan information
   */
  updateProductForm() {
    if (!this.productForm || !this.selectedPlan) return;

    // Remove all existing selling plan inputs
    const existingSellingPlans = this.productForm.querySelectorAll('input[name="selling_plan"]');
    existingSellingPlans.forEach(input => input.remove());

    // Remove existing subscription properties
    const existingProps = this.productForm.querySelectorAll('input[name*="properties[_subscription"]');
    existingProps.forEach(input => input.remove());

    // Add selling plan if this is a subscription
    if (this.selectedPlan.sellingPlanId) {
      this.addHiddenInput('selling_plan', this.selectedPlan.sellingPlanId);
    }

    // Add custom properties for tracking
    this.addHiddenInput('properties[_subscription_plan]', this.selectedPlan.type);
    this.addHiddenInput('properties[_subscription_price]', this.selectedPlan.price);
    this.addHiddenInput('properties[_subscription_discount]', this.selectedPlan.discount);
  }

  /**
   * Add hidden input to form
   */
  addHiddenInput(name, value) {
    if (!this.productForm) return;
    
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    this.productForm.appendChild(input);
  }

  /**
   * Fire custom event for other integrations
   */
  fireSelectionEvent() {
    const event = new CustomEvent('rechargeCustomSelectionChanged', {
      detail: {
        plan: this.selectedPlan.type,
        price: this.selectedPlan.price,
        discount: this.selectedPlan.discount,
        sellingPlanId: this.selectedPlan.sellingPlanId,
        productId: this.productId
      },
      bubbles: true
    });
    
    document.dispatchEvent(event);
    
    // Also fire on the widget element
    this.widget.dispatchEvent(event);
  }

  /**
   * Get current selection
   */
  getCurrentSelection() {
    return this.selectedPlan;
  }

  /**
   * Programmatically select a plan
   */
  selectPlan(planType) {
    const radio = this.widget.querySelector(`input[value="${planType}"]`);
    if (radio) {
      radio.checked = true;
      this.handlePlanSelection(radio);
    }
  }
}

/**
 * Auto-initialize when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  window.rechargeCustomWidget = new RechargeCustomWidget();
  window.rechargeCustomWidget.init();
});

// Also initialize if DOM is already loaded
if (document.readyState !== 'loading') {
  setTimeout(() => {
    if (!window.rechargeCustomWidget) {
      window.rechargeCustomWidget = new RechargeCustomWidget();
      window.rechargeCustomWidget.init();
    }
  }, 100);
}